import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import HelpIcon from '../help/HelpIcon';
import UserProfileService from '../../services/UserProfileService';

const defaultAddress = {
  country: 'México',
  neighborhood: '',
  postal_code: '',
  city: '',
  street: ''
};

const defaultCompany = {
  name: 'Test Corp 27638d76',
  legal_name: '',
  taxid: '',
  employees_count: '1',
  contact_phone: '',
  contact_email: '',
  facebook: '',
  linkedin: '',
  physical_address: { ...defaultAddress },
  fiscal_address: { ...defaultAddress }
};

const countryOptions = [
  { value: 'México', label: 'México' },
  { value: 'Chile', label: 'Chile' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'Perú', label: 'Perú' },
  { value: 'Panamá', label: 'Panamá' },
  { value: 'Uruguay', label: 'Uruguay' },
  { value: 'Guatemala', label: 'Guatemala' }
];

const toTrimmedString = (value, fallback = '') => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const output = String(value).trim();
  return output.length > 0 ? output : fallback;
};

const toEmployeesCount = (value) => {
  if (value === null || value === undefined) {
    return defaultCompany.employees_count;
  }

  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return String(parsed);
  }

  const fallback = String(value).trim();
  return fallback.length > 0 ? fallback : defaultCompany.employees_count;
};

const normalizeAddress = (address) => {
  if (!address || typeof address !== 'object') {
    return { ...defaultAddress };
  }

  return {
    country: toTrimmedString(address.country, defaultAddress.country),
    neighborhood: toTrimmedString(address.neighborhood, defaultAddress.neighborhood),
    postal_code: toTrimmedString(
      address.postal_code ?? address.postalCode,
      defaultAddress.postal_code
    ),
    city: toTrimmedString(address.city, defaultAddress.city),
    street: toTrimmedString(address.street, defaultAddress.street)
  };
};

const normalizeCompany = (company = {}) => {
  const normalized = {
    ...defaultCompany,
    physical_address: { ...defaultAddress },
    fiscal_address: { ...defaultAddress }
  };

  const employeesSource =
    company.employees_count ??
    company.employeesCount ??
    (company.employees && typeof company.employees === 'object'
      ? company.employees.count
      : company.employees);

  normalized.name = toTrimmedString(
    company.name ?? company.legal_name,
    defaultCompany.name
  );
  normalized.legal_name = toTrimmedString(
    company.legal_name,
    defaultCompany.legal_name
  );
  normalized.taxid = toTrimmedString(
    company.taxid ?? company.taxId,
    defaultCompany.taxid
  );
  normalized.employees_count = toEmployeesCount(employeesSource);
  normalized.contact_phone = toTrimmedString(
    company.contact_phone ?? company.contactPhone,
    defaultCompany.contact_phone
  );
  normalized.contact_email = toTrimmedString(
    company.contact_email ?? company.contactEmail,
    defaultCompany.contact_email
  );
  normalized.facebook = toTrimmedString(company.facebook, defaultCompany.facebook);
  normalized.linkedin = toTrimmedString(company.linkedin, defaultCompany.linkedin);
  normalized.physical_address = normalizeAddress(
    company.physical_address ?? company.physicalAddress
  );
  normalized.fiscal_address = normalizeAddress(
    company.fiscal_address ?? company.fiscalAddress
  );

  return normalized;
};

const CompanyProfileSection = () => {
  const [formData, setFormData] = useState(() => normalizeCompany());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadCompany = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const profile = await UserProfileService.getProfile();
        if (!isMounted) {
          return;
        }

        const normalized = normalizeCompany(profile?.company);
        setFormData(normalized);
        setIsEditMode(false);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Error al cargar los datos de la empresa:', err);
        setError('No se pudieron cargar los datos de la empresa.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCompany();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const timeout = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(timeout);
  }, [success]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (error) {
      setError(null);
    }
    if (success) {
      setSuccess(null);
    }
  };

  const handleAddressChange = (section, field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    if (error) {
      setError(null);
    }
    if (success) {
      setSuccess(null);
    }
  };

  const handleEditClick = () => {
    setSuccess(null);
    setError(null);
    setIsEditMode(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setSuccess(null);

    try {
      const updated = await UserProfileService.updateCompany(formData);
      const updatedCompany = updated?.company ?? updated;
      if (updatedCompany && typeof updatedCompany === 'object') {
        setFormData(normalizeCompany(updatedCompany));
      }
      setIsEditMode(false);
      setSuccess('Datos de la empresa actualizados correctamente.');
    } catch (err) {
      console.error('Error actualizando datos de la empresa:', err);
      setError('No se pudieron actualizar los datos de la empresa.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderReadOnly = () => {
    const formatValue = (value) => {
      const trimmed = toTrimmedString(value, '');
      return trimmed || '—';
    };

    const renderValue = (label, value) => (
      <Grid item xs={12} sm={6} key={label}>
        <Typography variant="caption" color="var(--wb-text-faint)">
          {label}
        </Typography>
        <Typography variant="body2" color="var(--wb-text-strong)" sx={{ mt: 0.5 }}>
          {formatValue(value)}
        </Typography>
      </Grid>
    );

    const renderAddressBlock = (title, address) => {
      const normalizedAddress = normalizeAddress(address);
      const street = toTrimmedString(normalizedAddress.street, '');
      const locationParts = [
        normalizedAddress.neighborhood,
        normalizedAddress.city,
        normalizedAddress.postal_code,
        normalizedAddress.country
      ]
        .map((part) => toTrimmedString(part, ''))
        .filter(Boolean);

      const displayStreet = street || '—';
      const displayLocation = locationParts.length > 0 ? locationParts.join(', ') : '—';

      return (
        <Grid item xs={12} md={6} key={title}>
          <Typography variant="subtitle2" color="var(--wb-x-text-ffffffcc, rgba(255,255,255,0.8))" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="var(--wb-text-strong)">
            {displayStreet}
          </Typography>
          <Typography variant="body2" color="var(--wb-text-strong)">
            {displayLocation}
          </Typography>
        </Grid>
      );
    };

    const displayFields = [
      ['Nombre comercial', formData.name],
      ['Razón social', formData.legal_name],
      ['RFC / Tax ID', formData.taxid],
      ['Número de empleados', formData.employees_count],
      ['Teléfono de contacto', formData.contact_phone],
      ['Email de contacto', formData.contact_email],
      ['Facebook', formData.facebook],
      ['LinkedIn', formData.linkedin]
    ];

    return (
      <Box>
        <Grid container spacing={2}>
          {displayFields.map(([label, value]) => renderValue(label, value))}
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          {renderAddressBlock('Dirección física', formData.physical_address)}
          {renderAddressBlock('Dirección fiscal', formData.fiscal_address)}
        </Grid>
      </Box>
    );
  };

  const renderForm = () => (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nombre comercial"
            name="name"
            value={formData.name}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="nombre-comercial" />
                </InputAdornment>
              )
            }}
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Razón social"
            name="legal_name"
            value={formData.legal_name}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="razón-social" />
                </InputAdornment>
              )
            }}
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="RFC / Tax ID"
            name="taxid"
            value={formData.taxid}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="rfc--tax-id" />
                </InputAdornment>
              )
            }}
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Número de empleados"
            name="employees_count"
            value={formData.employees_count}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ min: 1 }}
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="número-de-empleados" />
                </InputAdornment>
              )
            }}
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Teléfono de contacto"
            name="contact_phone"
            value={formData.contact_phone}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="teléfono-de-contacto" />
                </InputAdornment>
              )
            }}
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email de contacto"
            name="contact_email"
            value={formData.contact_email}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            type="email"
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="email-de-contacto" />
                </InputAdornment>
              )
            }}
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Facebook"
            name="facebook"
            value={formData.facebook}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="facebook--linkedin-opcionales" />
                </InputAdornment>
              )
            }}
            placeholder="https://facebook.com/tuempresa"
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="LinkedIn"
            name="linkedin"
            value={formData.linkedin}
            onChange={handleFieldChange}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
            InputProps={{
              sx: { color: 'var(--wb-text-strong)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="facebook--linkedin-opcionales" />
                </InputAdornment>
              )
            }}
            placeholder="https://linkedin.com/company/tuempresa"
            disabled={isSaving}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="var(--wb-x-text-ffffffcc, rgba(255,255,255,0.8))">
            Dirección física
          </Typography>
          <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="dirección-física" />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="País"
              value={formData.physical_address.country}
              onChange={handleAddressChange('physical_address', 'country')}
              fullWidth
              variant="outlined"
              size="small"
              select
              SelectProps={{ native: false }}
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            >
              {countryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Ciudad"
              value={formData.physical_address.city}
              onChange={handleAddressChange('physical_address', 'city')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Colonia / Barrio"
              value={formData.physical_address.neighborhood}
              onChange={handleAddressChange('physical_address', 'neighborhood')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Código postal"
              value={formData.physical_address.postal_code}
              onChange={handleAddressChange('physical_address', 'postal_code')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Calle"
              value={formData.physical_address.street}
              onChange={handleAddressChange('physical_address', 'street')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="var(--wb-x-text-ffffffcc, rgba(255,255,255,0.8))">
            Dirección fiscal
          </Typography>
          <HelpIcon module="perfil-empresa" topic="perfil-de-empresa" anchor="dirección-fiscal" />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="País"
              value={formData.fiscal_address.country}
              onChange={handleAddressChange('fiscal_address', 'country')}
              fullWidth
              variant="outlined"
              size="small"
              select
              SelectProps={{ native: false }}
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            >
              {countryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Ciudad"
              value={formData.fiscal_address.city}
              onChange={handleAddressChange('fiscal_address', 'city')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Colonia / Barrio"
              value={formData.fiscal_address.neighborhood}
              onChange={handleAddressChange('fiscal_address', 'neighborhood')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Código postal"
              value={formData.fiscal_address.postal_code}
              onChange={handleAddressChange('fiscal_address', 'postal_code')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Calle"
              value={formData.fiscal_address.street}
              onChange={handleAddressChange('fiscal_address', 'street')}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { color: 'var(--wb-text-faint)' }}}
              InputProps={{ sx: { color: 'var(--wb-text-strong)' }}}
              disabled={isSaving}
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 4
        }}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          sx={{
            bgcolor: 'var(--wb-x-bg-1f2937, #1F2937)',
            color: 'var(--wb-x-text-b0bec5, #B0BEC5)',
            '&:hover': { bgcolor: 'var(--wb-x-bg-273349, #273349)' },
            minWidth: 180
          }}
        >
          {isSaving ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: 'var(--wb-x-text-b0bec5, #B0BEC5)' }} />
              Guardando...
            </Box>
          ) : (
            'Guardar cambios'
          )}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'var(--wb-bg-panel)',
        p: 3,
        borderRadius: 0,
        borderTop: '1px solid var(--wb-overlay-08)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 1
        }}
      >
        <Typography variant="h6" color="var(--wb-text-strong)">
          Datos de la empresa
        </Typography>

        {!isLoading && !isEditMode && (
          <Button
            variant="outlined"
            onClick={handleEditClick}
            sx={{
              color: 'var(--wb-x-text-b0bec5, #B0BEC5)',
              borderColor: 'var(--wb-x-border-b0bec566, rgba(176,190,197,0.4))',
              '&:hover': {
                borderColor: 'var(--wb-x-border-b0bec5, #B0BEC5)',
                bgcolor: 'var(--wb-x-bg-b0bec514, rgba(176,190,197,0.08))'
              }
            }}
            startIcon={<EditIcon sx={{ color: 'var(--wb-x-text-b0bec5, #B0BEC5)' }} />}
          >
            Editar
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="var(--wb-text-soft)" sx={{ mb: 3 }}>
        Mantén la información fiscal y de contacto de tu organización al día.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 160
          }}
        >
          <CircularProgress size={36} sx={{ color: 'var(--wb-primary)' }} />
        </Box>
      ) : (
        <Box>
          {isEditMode ? renderForm() : renderReadOnly()}
        </Box>
      )}
    </Paper>
  );
};

export default CompanyProfileSection;
